/**
 * Which cached data belongs to a workspace, and which outlives one.
 *
 * This lives in its own module, rather than next to `switchWorkspace`, because
 * BOTH the tab performing a switch and every other tab reacting to it must
 * apply the identical rule. Keeping it here means there is one list; putting it
 * beside either consumer would make the other import that consumer and create a
 * cycle (`analyze:circular` catches it, which is how this file came to exist).
 */

/**
 * Query-key prefixes that are NOT workspace-scoped and must survive a switch.
 *
 * Clearing these is not merely wasteful, it is visible: the sidebar renders
 * `wallet.isLoading` / `isError` directly, so dropping the credit wallet makes
 * the credit line flash on every switch; and `brand-kits` is the switcher's OWN
 * list, so clearing it empties the dropdown the user is still looking at,
 * mid-interaction.
 *
 * `getCacheConfig` already gives user/profile/settings a 24h gcTime precisely
 * so they do not refetch. Honour that.
 */
export const WORKSPACE_INDEPENDENT_KEY_PREFIXES = [
  'brand-kits',
  'active-workspace-pointer',
  'effective-workspace-id',
  'user',
  'user-profile-company-name',
  'profile',
  'settings',
  'subscription',
  'credit-wallet',
  'credit-wallet-mode',
  'account-members',
] as const;

export function isWorkspaceIndependentKey(key: readonly unknown[]): boolean {
  const head = key[0];
  return (
    typeof head === 'string' &&
    WORKSPACE_INDEPENDENT_KEY_PREFIXES.some((p) => head === p || head.startsWith(`${p}:`))
  );
}
